"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function SyncForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="223960" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
          <svg className="size-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-foreground">
            Melding Mottatt
          </h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md border-primary/50 text-primary hover:bg-primary/10">
          Send en ny melding
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Navn er påkrevd"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Ditt Navn</FormLabel>
              <FormControl>
                <Input placeholder="Ola Nordmann" {...field} className="bg-slate-900/50 border-white/10 focus-visible:ring-ring rounded-md text-white" />
              </FormControl>
              <FormDescription className="text-xs text-slate-400">Fornavn og etternavn</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-post er påkrevd",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Vennligst oppgi en gyldig e-postadresse"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">E-postadresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ola@eiendom.no" {...field} className="bg-slate-900/50 border-white/10 focus-visible:ring-ring rounded-md text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Eiendomsmegler / Firma</FormLabel>
              <FormControl>
                <Input placeholder="Xora Eiendom" {...field} className="bg-slate-900/50 border-white/10 focus-visible:ring-ring rounded-md text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+47 900 00 000" {...field} className="bg-slate-900/50 border-white/10 focus-visible:ring-ring rounded-md text-white" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Vennligst beskriv dine behov"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Hva ønsker du å automatisere?</FormLabel>
            <FormControl>
              <Textarea placeholder="Fortell oss litt om dine nåværende prosesser eller ønsket AI-løsning..." className="min-h-[120px] bg-slate-900/50 border-white/10 focus-visible:ring-ring rounded-md resize-y text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-semibold group bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-all duration-300 rounded-md shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Kobler til...
          </> : <>
            Start Automatiseringen
            <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
          </>}
      </Button>
    </Form>;
}
export default function SyncWithUs() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="la-oss-snakke-om-fremtiden" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.15]"></div>
        
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] opacity-40 mix-blend-screen"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-accent/10 rounded-full blur-[100px] opacity-20 mix-blend-screen"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 space-y-10 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/40 border border-white/10 backdrop-blur-sm">
                <span className="relative flex size-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full size-2.5 bg-cyan-500"></span>
                </span>
                <span className="text-xs font-mono font-medium text-accent tracking-wider uppercase">
                  AI-Status: Online
                </span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tighter text-foreground leading-[1.1]">
                La oss forme <br className="hidden lg:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  fremtiden din.
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
                Direkte kanal for eiendomsmeglere som ønsker å lede an i den digitale revolusjonen. Vårt AI-team står klart til å analysere dine behov.
              </p>
            </div>

            <div className="space-y-6 pt-4 border-t border-white/10">
              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-slate-900/60 border border-white/10 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
                  <Mail className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Send oss e-post</p>
                  <p className="text-base font-default font-medium text-foreground">hei@xora.no</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-slate-900/60 border border-white/10 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
                  <Phone className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Ring oss</p>
                  <p className="text-base font-default font-medium text-foreground">+47 95 42 42 26</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="mt-1 p-3 rounded-xl bg-slate-900/60 border border-white/10 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Hovedkontor</p>
                  <p className="text-base font-default font-medium text-foreground">Skotselv, Øvre Eiker</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative group">
              {}
              <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-b from-primary/40 via-accent/20 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500 blur-[2px]"></div>
              
              <div className="relative bg-slate-900/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 md:p-10 shadow-4xl overflow-hidden">
                {}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                
                <div className="mb-8">
                  <h3 className="text-2xl font-default font-bold text-foreground mb-2">Kontakt oss</h3>
                  <p className="text-sm text-slate-400 font-default">
                    Fyll ut skjemaet under, så vil vårt tekniske team kontakte deg innen 24 timer for en uforpliktende prat.
                  </p>
                </div>

                <FormProvider formKey="future_form_contact" formId="future_form_contact" sectionName="la-oss-snakke-om-fremtiden" defaultValues={{
                name: "",
                email: "",
                company: "",
                phone: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Start Automatiseringen" successMessage="Din forespørsel er sendt. Vårt team vil analysere dine behov og ta kontakt for å diskutere din AI-strategi.">
                  <SyncForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}